import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, isOnline } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { isNative } from '../lib/platform';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const { currentMember, householdId } = useAuth();
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const listenersRef = useRef([]);

  const native = isNative();

  const supported = native
    ? true
    : typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      !!VAPID_PUBLIC_KEY;

  // Check current subscription status on mount
  useEffect(() => {
    if (!supported) {
      setLoading(false);
      return;
    }

    if (native) {
      // On native, check if we have a stored subscription for this member
      (async () => {
        if (!currentMember) { setLoading(false); return; }
        try {
          const { data } = await supabase
            .from('push_subscriptions')
            .select('id')
            .eq('member_id', currentMember.id)
            .eq('platform', 'ios')
            .limit(1);
          setIsSubscribed(!!data?.length);
        } catch { /* ignore */ }
        setLoading(false);
      })();
    } else {
      // Web: check PushManager subscription
      (async () => {
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          setIsSubscribed(!!subscription);
        } catch { /* SW not ready yet */ }
        setLoading(false);
      })();
    }
  }, [supported, native, currentMember]);

  const subscribe = useCallback(async () => {
    if (!supported || !currentMember || !householdId || !isOnline()) return false;

    setLoading(true);

    if (native) {
      // Native iOS push via Capacitor
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');

        const permResult = await PushNotifications.requestPermissions();
        if (permResult.receive !== 'granted') {
          setPermission('denied');
          setLoading(false);
          return false;
        }
        setPermission('granted');

        // Listen for registration event
        return new Promise((resolve) => {
          const regListener = PushNotifications.addListener('registration', async (token) => {
            try {
              const { error } = await supabase.from('push_subscriptions').upsert(
                {
                  member_id: currentMember.id,
                  household_id: householdId,
                  platform: 'ios',
                  device_token: token.value,
                  endpoint: null,
                  p256dh: null,
                  auth: null,
                },
                { onConflict: 'device_token' }
              );

              if (error) {
                console.error('Failed to save push subscription:', error.message);
                setLoading(false);
                resolve(false);
                return;
              }

              setIsSubscribed(true);
              setLoading(false);
              resolve(true);
            } catch (err) {
              console.error('Push subscription save failed:', err);
              setLoading(false);
              resolve(false);
            }
          });

          const errListener = PushNotifications.addListener('registrationError', (err) => {
            console.error('Push registration error:', err);
            setLoading(false);
            resolve(false);
          });

          listenersRef.current.push(regListener, errListener);

          PushNotifications.register();
        });
      } catch (err) {
        console.error('Native push subscription failed:', err);
        setLoading(false);
        return false;
      }
    } else {
      // Web push via VAPID
      try {
        const result = await Notification.requestPermission();
        setPermission(result);
        if (result !== 'granted') {
          setLoading(false);
          return false;
        }

        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });

        const keys = subscription.toJSON().keys;

        const { error } = await supabase.from('push_subscriptions').upsert(
          {
            member_id: currentMember.id,
            household_id: householdId,
            platform: 'web',
            endpoint: subscription.endpoint,
            p256dh: keys.p256dh,
            auth: keys.auth,
          },
          { onConflict: 'endpoint' }
        );

        if (error) {
          console.error('Failed to save push subscription:', error.message);
          await subscription.unsubscribe();
          setLoading(false);
          return false;
        }

        setIsSubscribed(true);
        setLoading(false);
        return true;
      } catch (err) {
        console.error('Push subscription failed:', err);
        setLoading(false);
        return false;
      }
    }
  }, [supported, native, currentMember, householdId]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return false;

    setLoading(true);

    if (native) {
      // Native: delete subscription rows for this member + platform
      try {
        if (isOnline() && currentMember) {
          await supabase
            .from('push_subscriptions')
            .delete()
            .eq('member_id', currentMember.id)
            .eq('platform', 'ios');
        }
        setIsSubscribed(false);
        setLoading(false);
        return true;
      } catch (err) {
        console.error('Native push unsubscribe failed:', err);
        setLoading(false);
        return false;
      }
    } else {
      // Web: unsubscribe from PushManager and delete from Supabase
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (subscription) {
          if (isOnline()) {
            await supabase
              .from('push_subscriptions')
              .delete()
              .eq('endpoint', subscription.endpoint);
          }
          await subscription.unsubscribe();
        }

        setIsSubscribed(false);
        setLoading(false);
        return true;
      } catch (err) {
        console.error('Push unsubscribe failed:', err);
        setLoading(false);
        return false;
      }
    }
  }, [supported, native, currentMember]);

  // Clean up Capacitor listeners on unmount
  useEffect(() => {
    return () => {
      listenersRef.current.forEach(async (listener) => {
        try { (await listener).remove(); } catch { /* ignore */ }
      });
      listenersRef.current = [];
    };
  }, []);

  return { supported, permission, isSubscribed, subscribe, unsubscribe, loading };
}
